<template>
  <v-container
    class="d-flex"
    fluid
  >
    <v-row>
      <v-col
        :cols="isCalendarVisible ? 12 : undefined"
        :md="isCalendarVisible ? 6 : undefined"
      >
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                ref="docketEntryTypeOptions"
                v-model="docketEntry.docket_entry_type_id"
                :disabled="!isDocketEntryTypeEditable"
                :items="docketEntryTypeOptions"
                :label="actionTypeLabel"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.docket_entry_type_id)"
                @input="_ => v$.docketEntry.docket_entry_type_id.$touch()"
                @blur="_ => v$.docketEntry.docket_entry_type_id.$touch()"
              >
                <template #selection="{ item }">
                  <span>
                    {{ item.text }}
                    <v-icon
                      v-if="item.has_workflows"
                      class="ml-1"
                      color="black"
                      size="18"
                    >
                      mdi-arrow-decision-outline
                    </v-icon>
                    <v-icon
                      v-if="item.has_checklist"
                      class="ml-1"
                      color="black"
                      size="18"
                    >
                      mdi-format-list-checks
                    </v-icon>
                    <v-icon
                      v-if="item.has_notifications"
                      class="ml-1"
                      color="black"
                      size="18"
                    >
                      mdi-message-outline
                    </v-icon>
                  </span>
                </template>
                <template #item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ item.text }}
                      <v-icon
                        v-if="item.has_workflows"
                        class="ml-1"
                        color="black"
                        size="18"
                      >
                        mdi-arrow-decision-outline
                      </v-icon>
                      <v-icon
                        v-if="item.has_checklist"
                        class="ml-1"
                        color="black"
                        size="18"
                      >
                        mdi-format-list-checks
                      </v-icon>
                      <v-icon
                        v-if="item.has_notifications"
                        class="ml-1"
                        color="black"
                        size="18"
                      >
                        mdi-message-outline
                      </v-icon>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col
              v-if="!isNote"
              cols="12"
              :style="isAppointment ? 'order: -1' : ''"
            >
              <UserSelect
                ref="assignees"
                v-model="selectedUsers"
                :allowed-users="allowedUsers"
                chips
                deletable-chips
                hide-disabled
                hide-selected
                label="Assignees *"
                multiple
                no-data-text="No users available"
                only-assignable
                outlined
                show-groups
                :error-messages="getErrorMessages(v$.selectedUsers)"
                @input="_ => v$.selectedUsers.$touch()"
                @blur="_ => v$.selectedUsers.$touch()"
              >
                <template
                  v-if="isAppointment"
                  #selection="data"
                >
                  <v-tooltip
                    bottom
                    color="red darken-4"
                    dark
                    :disabled="canBeAssigned(data.item.value)"
                  >
                    <template #activator="tooltip">
                      <v-chip
                        close
                        :class="{'red darken-4': !canBeAssigned(data.item.value)}"
                        :dark="!canBeAssigned(data.item.value)"
                        :input-value="data.selected"
                        v-bind="{ ...data.attrs, ...tooltip.attrs }"
                        v-on="tooltip.on"
                        @click:close="_ => removeAssignee(data.item.value)"
                      >
                        <v-icon
                          v-if="!canBeAssigned(data.item.value)"
                          left
                        >
                          mdi-alert-circle-outline
                        </v-icon>
                        {{ data.item.fullName }}
                      </v-chip>
                    </template>
                    <span>
                      This user hasn't given you permission to create<br>
                      AdHoc Appointments and cannot be set as an Assignee!
                    </span>
                  </v-tooltip>
                </template>
              </UserSelect>
              <v-checkbox
                v-if="enableRequireAllDone"
                v-model="docketEntry.require_all_done"
                dense
              >
                <template #label>
                  <div>
                    Require all assignees to complete {{ docketEntry.action_type }}
                    <v-tooltip
                      bottom
                      max-width="400"
                    >
                      <template #activator="{ on }">
                        <v-icon
                          small
                          v-on="on"
                        >
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      If you select this option all Assignees will be required to complete the {{ docketEntry.action_type }} in order for it to be marked as Complete, otherwise it will be set as Complete as long as a single Assignee completes it.
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
            </v-col>
            <v-col
              v-if="isAppointment"
              cols="12"
            >
              <v-autocomplete
                v-model="selectedParties"
                chips
                deletable-chips
                :disabled="!areAttendeesEditable"
                hide-selected
                :items="partyOptions"
                label="Attendees *"
                multiple
                no-data-text="No Parties available"
                outlined
                :error-messages="getErrorMessages(v$.selectedParties)"
                @input="_ => v$.selectedParties.$touch()"
                @blur="_ => v$.selectedParties.$touch()"
              />
            </v-col>
          </v-row>
          <v-row v-if="areSecondaryFieldsVisible">
            <v-col
              cols="12"
              :sm="isTask || isNote ? 'auto' : 6"
              :style="isTask || isNote ? 'min-width: 220px;' : 'min-width: 230px;'"
            >
              <DateField
                v-model="docketEntry.action_date"
                :disabled="!isActionDateEditable"
                :label="actionDateLabel"
                outlined
                show-adjust-days
                :error-messages="getErrorMessages(v$.docketEntry.action_date)"
                @input="_ => v$.docketEntry.action_date.$touch()"
                @blur="_ => v$.docketEntry.action_date.$touch()"
              />
            </v-col>
            <v-col
              v-if="enableTimeFields"
              class="dateField xs"
              cols="6"
              sm="3"
            >
              <TimeField
                v-model="docketEntry.event_time_start"
                :disabled="!isEventTimeEditable"
                label="Start Time *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.event_time_start)"
                @input="_ => v$.docketEntry.event_time_start.$touch()"
                @blur="_ => v$.docketEntry.event_time_start.$touch()"
              />
            </v-col>
            <v-col
              v-if="enableTimeFields"
              class="dateField xs"
              cols="6"
              sm="3"
            >
              <TimeField
                v-model="docketEntry.event_time_end"
                :disabled="!isEventTimeEditable"
                label="End Time *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.event_time_end)"
                @input="_ => v$.docketEntry.event_time_end.$touch()"
                @blur="_ => v$.docketEntry.event_time_end.$touch()"
              />
            </v-col>
            <v-col
              v-if="enableTimeFields"
              cols="12"
              :sm="enableTimeFields ? 12 : 6"
              class="dateField xs"
            >
              <v-autocomplete
                v-model="docketEntry.timezone"
                :disabled="!isEventTimeEditable"
                :items="timezoneOptions"
                label="Timezone *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.timezone)"
                @input="_ => v$.docketEntry.timezone.$touch()"
                @blur="_ => v$.docketEntry.timezone.$touch()"
              />
            </v-col>
            <v-col
              v-if="enableLocationField"
              cols="12"
              class="dateField xs d-flex"
            >
              <v-menu
                v-if="appointmentLocations.length"
                bottom
                right
                offset-y
              >
                <template #activator="{ on, attrs }">
                  <v-btn
                    class="me-4"
                    depressed
                    style="min-height: 56px;"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-map-marker-outline</v-icon>
                    <v-icon>mdi-menu-down</v-icon>
                  </v-btn>
                </template>

                <v-list
                  dense
                  nav
                >
                  <template v-for="(location, index) of appointmentLocations">
                    <v-divider
                      v-if="index > 0"
                      :key="`divider_${location.id}`"
                      class="mb-1"
                    />
                    <v-list-item
                      :key="`item_${location.id}`"
                      @click="_ => selectAppointmentLocation(location.id)"
                    >
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-regular">
                          {{ location.name }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </v-menu>
              <v-text-field
                v-model="docketEntry.appointment_location"
                counter="255"
                :disabled="!isAppointmentLocationEditable"
                label="Location *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.appointment_location)"
                @input="_ => v$.docketEntry.appointment_location.$touch()"
                @blur="_ => v$.docketEntry.appointment_location.$touch()"
              />
            </v-col>
            <v-col
              cols="12"
              :sm="isTask || isNote ? true : 12"
              :style="isTask || isNote ? 'min-width: 90px;' : ''"
            >
              <v-text-field
                v-model="docketEntry.agency_num"
                outlined
                label="Number"
              />
            </v-col>
            <v-col cols="12">
              <DocketEntryEditorComment
                v-model="docketEntry.comment"
                :legal-case-id="docketEntry.legal_case_id"
              />
            </v-col>
          </v-row>
          <v-row v-if="userCan.createInternalDocketEntry">
            <v-col cols="auto">
              <v-checkbox
                v-model="docketEntry.is_internal"
                dense
              >
                <template #label>
                  <div>
                    Internal
                    <v-tooltip
                      bottom
                      max-width="400"
                    >
                      <template #activator="{ on }">
                        <v-icon
                          small
                          v-on="on"
                        >
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      If you select this option the Docket Entry will only be visible to Users with the
                      <strong>View Internal Docket Entries</strong> permission.
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
      <v-col
        v-if="isCalendarVisible"
        cols="12"
        md="6"
      >
        <DocketEntryEditorAvailability
          :availability-slot-id.sync="selectedAvailabilitySlotId"
          filter-by-type
          :users-that-need-slots.sync="usersThatNeedSlots"
        />
      </v-col>
    </v-row>

    <ConfirmationDialog
      v-if="isDefaultValuesSelectorDialogVisible"
      v-model="isDefaultValuesSelectorDialogVisible"
      title="Confirmation Request"
      @confirmed="_ => onReplaceWithDocketEntryTypeDefaults()"
    >
      <template #message>
        <p>
          The {{ actionTypeLabel }} <strong>{{ docketEntryType.name }}</strong> contains default values that can
          replace the existing ones.
        </p>
        <p>Please select what default values should be used:</p>
        <div>
          <div
            v-for="item of replaceableValues"
            :key="item.value"
          >
            <v-checkbox
              v-model="selectedDefaultValues"
              hide-details
              :label="item.label"
              :value="item.value"
            />
          </div>
        </div>
      </template>
    </ConfirmationDialog>
  </v-container>
</template>

<script>
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters } from 'vuex'
import { startCase, unset } from 'lodash'
import Vue from 'vue'
import moment from 'moment'
import { useVuelidate } from '@vuelidate/core'

import actions from '@/data/Permissions/enums.json'
import { partyName } from '@/utilities'

import { CALENDAR_NAMESPACE } from '@/store/calendar'
import { CHECKLIST_NAMESPACE } from '@/store/checklist'
import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SHARED_NAMESPACE } from '@/store/shared'
import { USER_NAMESPACE } from '@/store/user'

import ConfirmationDialog from '@/components/Dialogs/Common/ConfirmationDialog.vue'
import DateField from '@/components/Fields/DateField.vue'
import DocketEntryEditorAvailability from './DocketEntryEditorAvailability.vue'
import DocketEntryEditorComment from './DocketEntryEditorComment.vue'
import TimeField from '@/components/Fields/TimeField.vue'
import UserSelect from '@/components/Fields/UserSelect.vue'

export default {
  name: 'DocketEntryEditorOverview',

  components: {
    ConfirmationDialog,
    DateField,
    DocketEntryEditorAvailability,
    DocketEntryEditorComment,
    TimeField,
    UserSelect,
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      appointmentLocations: [],
      isDefaultValuesSelectorDialogVisible: false,
      libraryChecklists: null,
      replaceableValues: [],
      selectedAvailabilitySlotId: null,
      selectedDefaultValues: [],
      selectedParties: [],
      selectedUsers: [],
      timezoneOptions: [],
      usersThatNeedSlots: [],
    }
  },

  validations () {
    return {
      docketEntry: {
        action_date: {
          required,
        },

        appointment_location: {
          maxLength: maxLength(255),
          required: helpers.withMessage(
            'A Location must be supplied',
            (value, _, vm) => vm.isAppointment ? helpers.req(value) : true
          ),
        },

        docket_entry_type_id: {
          required,
        },

        event_time_end: {
          afterStartTime: helpers.withMessage(
            'The provided time must com after the Start Time',
            (value, _, vm) => {
              if (!vm.enableTimeFields || !value || !vm.docketEntry.event_time_start) return true

              const et = moment(value, 'LT')
              if (!et.isValid()) return true

              const st = moment(vm.docketEntry.event_time_start, 'LT')
              if (!st.isValid()) return true

              return et.isAfter(st)
            }
          ),

          required: helpers.withMessage(
            'Value is required',
            (value, _, vm) => vm.enableTimeFields ? helpers.req(value) : true
          ),
        },

        event_time_start: {
          required: helpers.withMessage(
            'Value is required',
            (value, _, vm) => vm.enableTimeFields ? helpers.req(value) : true
          ),
        },

        timezone: {
          required: helpers.withMessage(
            'Value is required',
            (value, _, vm) => vm.enableTimeFields ? helpers.req(value) : true
          ),
        },
      },

      selectedParties: {
        required: helpers.withMessage(
          'Please select at least one Party',
          (value, _, vm) => vm.isAppointment ? vm.selectedParties.length > 0 : true
        ),
      },

      selectedUsers: {
        permissions: helpers.withMessage(
          'Some Users cannot be Assignees',
          (value, _, vm) => !vm.isAppointment || this.areAppointmentFieldsMissing || vm.usersThatNeedSlots.length === 0
        ),

        required: requiredIf(!this.isNote),
      },
    }
  },

  computed: {
    actionDateLabel () {
      return `Date of ${this.docketEntry.action_type} *`
    },

    actionTypeLabel () {
      return `${startCase(this.docketEntry.action_type)} Type`
    },

    adHocAllowedUsers () {
      return [
        this.currentUser.id,
        ...this.receivedPermissionsFor(this.currentUser.id, 'CreateAdHocAppointments').map(gp => gp.fromUserId),
      ]
    },

    allowedUsers () {
      if (!this.isAppointment) return null

      return this.adHocAllowedUsers.
        concat(this.bookingAllowedUsers.filter(a => !this.adHocAllowedUsers.includes(a))).
        concat(this.selectedUsers)
    },

    areAppointmentFieldsMissing () {
      return !(this.docketEntry.docket_entry_type_id &&
            this.docketEntry.action_date &&
            this.docketEntry.event_time_start &&
            this.docketEntry.event_time_end)
    },

    areAttendeesEditable () {
      return !this.docketEntry.id && !this.isDocketEntryEditorInRescheduleMode
    },

    areSecondaryFieldsVisible () {
      if (this.isNote) return true

      if (this.isAppointment) {
        return this.selectedUsers.length &&
          this.selectedParties.length &&
          this.docketEntry.docket_entry_type_id
      }

      return this.selectedUsers.length &&
        this.docketEntry.docket_entry_type_id
    },

    assignees () {
      return (this.docketEntry.docket_entries_assignments || []).
        reduce((res, dea) => {
          if (!dea._destroy) res.push(dea.user_id)
          return res
        }, [])
    },

    attendees () {
      return (this.docketEntry.attendances || []).
        reduce((res, dea) => {
          if (!dea._destroy) res.push(dea.party_id)
          return res
        }, [])
    },

    bookingAllowedUsers () {
      return this.receivedPermissionsFor(this.currentUser.id, 'BookAvailabilitySlots').map(gp => gp.fromUserId)
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    docketEntryType () {
      return this.filteredDocketEntryTypes.find(det => det.id === this.docketEntry.docket_entry_type_id)
    },

    docketEntryTypeOptions () {
      return this.filteredDocketEntryTypes.
        reduce((res, det) => {
          if (!det.all_legal_case_types && !det.legal_case_type_ids.includes(this.legalCase?.legal_case_type_id)) {
            return res
          }

          res.push({
            has_checklist: !!det.checklist,
            has_notifications: !!det.docket_entry_type_notifications.length,
            has_workflows: !!det.workflows.length,
            text: det.name,
            value: det.id,
          })

          return res
        }, [])
    },

    enableLocationField () {
      return this.isAppointment
    },

    enableRequireAllDone () {
      return (this.isEvent || this.isTask) && this.assignees.length > 1
    },

    enableTimeFields () {
      return this.isAppointment || this.isEvent
    },

    filteredDocketEntryTypes () {
      return this.docketEntryTypes.filter(det => {
        return (
          det.action_type === this.docketEntry.action_type &&
            det.archived === false
        )
      })
    },

    isActionDateEditable () {
      return !(this.isAppointment && this.docketEntry.id)
    },

    isAppointment () {
      return this.docketEntry.action_type === 'Appointment'
    },

    isAppointmentLocationEditable () {
      return !(this.isAppointment && this.docketEntry.id)
    },

    isCalendarVisible () {
      return this.isAppointment
    },

    isDocketEntryTypeEditable () {
      return !this.docketEntry.id && !(this.isDocketEntryEditorInCloneMode || this.isDocketEntryEditorInRescheduleMode)
    },

    isEvent () {
      return this.docketEntry.action_type === 'Event'
    },

    isEventTimeEditable () {
      return !(this.isAppointment && this.docketEntry.id)
    },

    isFormValid () {
      return !this.v$.$invalid
    },

    isNote () {
      return this.docketEntry.action_type === 'Note'
    },

    isTask () {
      return this.docketEntry.action_type === 'Task'
    },

    legalCase () {
      return this.legalCases.find(lc => lc.id === this.docketEntry.legal_case_id)
    },

    legalCaseParties () {
      return this.legalCase?.parties || []
    },

    partyOptions () {
      return (this.legalCaseParties || []).map(party => {
        return {
          text: partyName(party),
          value: party.id,
        }
      })
    },

    userCan () {
      return {
        createInternalDocketEntry: this.can(actions.Platform.DocketEntry.Internal.Create),
        viewInternalDocketEntry: this.can(actions.Platform.DocketEntry.Internal.View),
      }
    },

    ...mapGetters([
      'currentUser',
    ]),

    ...mapGetters(CALENDAR_NAMESPACE, [
      'receivedPermissionsFor',
    ]),

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
      'isDocketEntryEditorInCloneMode',
      'isDocketEntryEditorInRescheduleMode',
      'originalDocketEntry',
    ]),

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCases',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),
  },

  watch: {
    'docketEntry.attendances': {
      handler (val) {
        this.selectedParties = (val || []).reduce((res, dea) => {
          if (!dea._destroy) res.push(dea.party_id)
          return res
        }, [])
      },

      immediate: true,
    },

    'docketEntry.docket_entries_assignments': {
      deep: true,

      handler (val) {
        this.selectedUsers = (val || []).reduce((res, dea) => {
          if (!dea._destroy) res.push(dea.user_id)
          return res
        }, [])
      },

      immediate: true,
    },

    'docketEntry.timezone' (val) {
      if (!this.timezoneOptions.find(tzo => tzo.value === val)) {
        this.docketEntry.timezone = this.originalDocketEntry.timezone
      }
    },

    docketEntryType (newVal, oldVal) {
      if (newVal?.id === oldVal?.id) return
      this.onDocketEntryTypeChange()
    },

    isFormValid: {
      handler (value) {
        this.$emit('update:valid', value)
      },

      immediate: true,
    },

    selectedParties (val) {
      const addedParties = val.filter(pid => !this.attendees.includes(pid))
      addedParties.forEach(pid => this.addAttendee(pid))

      const removedParties = this.attendees.filter(pid => !val.includes(pid))
      removedParties.forEach(pid => this.removeAttendee(pid))
    },

    selectedUsers (val) {
      const addedUsers = val.filter(uid => !this.assignees.includes(uid))
      addedUsers.forEach(uid => this.addAssignee(uid))

      const removedUsers = this.assignees.filter(uid => !val.includes(uid))
      removedUsers.forEach(uid => this.removeAssignee(uid))
    },

    usersThatNeedSlots () {
      if (this.selectedUsers.length) this.v$.selectedUsers.$touch()
    },
  },

  async created () {
    await this.loadTimezoneOptions()
    this.appointmentLocations = await this.fetchAppointmentLocations()
  },

  mounted () {
    this.$nextTick().then(_ => {
      if (this.isAppointment) this.$refs.assignees.focus()
      else this.$refs.docketEntryTypeOptions.focus()
    })
  },

  methods: {
    addAssignee (userId) {
      const assignment = this.docketEntry.docket_entries_assignments.find(dea => dea.user_id === userId)

      if (!assignment) {
        this.docketEntry.docket_entries_assignments.push({ user_id: userId })
      } else if (assignment._destroy) {
        unset(assignment, '_destroy')
      }
    },

    addAttendee (partyId) {
      const attendance = this.docketEntry.attendances.find(dea => dea.party_id === partyId)

      if (!attendance) {
        this.docketEntry.attendances.push({ party_id: partyId })
      } else if (attendance._destroy) {
        unset(attendance, '_destroy')
      }
    },

    canBeAssigned (userId) {
      if (this.areAppointmentFieldsMissing) { return true }
      if (!this.usersThatNeedSlots.includes(userId)) return true
    },

    getErrorMessages (validator) {
      return validator?.$errors.map(e => e.$message)
    },

    async loadDefaultDocketEntryChecklist () {
      if (!this.libraryChecklists) {
        await this.getChecklists().then(data => { this.libraryChecklists = data })
      }

      const checklist = this.libraryChecklists.find(lc => lc.id === this.docketEntryType.checklist.id)

      this.docketEntry.checklist = {
        items: checklist.items,
        title: checklist.title,
      }
    },

    async loadTimezoneOptions () {
      this.timezoneOptions = await this.getOptions({ field: 'timezone', model: 'SiteSetting::Timezone' })
    },

    onDocketEntryTypeChange () {
      if (!this.docketEntryType) return

      this.replaceableValues = []
      this.selectedDefaultValues = []

      if (this.docketEntryType.default_comment) {
        if (!this.docketEntry.comment?.trim()?.length) {
          this.docketEntry.comment = this.docketEntryType.default_comment
        } else {
          this.replaceableValues.push({ label: 'Comment', value: 'comment' })
        }
      }

      if (this.docketEntryType.checklist?.id) {
        if (!this.docketEntry.checklist) {
          this.loadDefaultDocketEntryChecklist()
        } else {
          this.replaceableValues.push({ label: 'Checklist items', value: 'checklist' })
        }
      }

      if (this.replaceableValues.length) this.isDefaultValuesSelectorDialogVisible = true
    },

    onReplaceWithDocketEntryTypeDefaults () {
      if (this.selectedDefaultValues.includes('comment')) {
        this.docketEntry.comment = this.docketEntryType.default_comment
      }

      if (this.selectedDefaultValues.includes('checklist')) this.loadDefaultDocketEntryChecklist()

      this.isDefaultValuesSelectorDialogVisible = false
    },

    removeAssignee (userId) {
      const index = this.docketEntry.docket_entries_assignments.findIndex(dea => dea.user_id === userId)
      if (index < 0) return

      const assignment = this.docketEntry.docket_entries_assignments[index]

      if (assignment.id) {
        Vue.set(assignment, '_destroy', true)
      } else {
        this.docketEntry.docket_entries_assignments.splice(index, 1)
      }
    },

    removeAttendee (partyId) {
      const index = this.docketEntry.attendances.findIndex(dea => dea.party_id === partyId)
      if (index < 0) return

      const attendance = this.docketEntry.attendances[index]

      if (attendance.id) {
        Vue.set(attendance, '_destroy', true)
      } else {
        this.docketEntry.attendances.splice(index, 1)
      }
    },

    selectAppointmentLocation (appointmentLocationId) {
      this.docketEntry.appointment_location = this.appointmentLocations.
        find(al => al.id === appointmentLocationId).
        location
    },

    async validate () {
      await this.v$.$validate()
    },

    ...mapActions(CHECKLIST_NAMESPACE, [
      'getChecklists',
    ]),

    ...mapActions(DOCKET_ENTRY_NAMESPACE, [
      'fetchAppointmentLocations',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getOptions',
    ]),
  },
}
</script>
